import { z } from 'zod';

import { Id, IsoDateTime, Paise } from '../common/primitives';

export { Paise } from '../common/primitives';

// ── Enums ───────────────────────────────────────────────────────────────────

export const LifecycleEventSourceSchema = z.enum(['CUSTOMER', 'VENDOR', 'PLATFORM', 'SYSTEM']);
export type LifecycleEventSource = z.infer<typeof LifecycleEventSourceSchema>;

/**
 * Lifecycle "stages" — a derived view of where the customer is in their journey, computed from
 * order history + activity. Used to drive segmentation and the right automation cadence.
 *
 *   NEW          — registered but no orders yet
 *   ACTIVE       — placed an order in the last 30 days
 *   REPEAT       — 2+ orders, last one within 60 days
 *   AT_RISK      — last order 60–180 days ago
 *   DORMANT      — last order > 180 days ago
 *   CHURNED      — last order > 365 days ago
 */
export const CustomerLifecycleStageSchema = z.enum(['NEW', 'ACTIVE', 'REPEAT', 'AT_RISK', 'DORMANT', 'CHURNED']);
export type CustomerLifecycleStage = z.infer<typeof CustomerLifecycleStageSchema>;

// ── Response shapes ────────────────────────────────────────────────────────

export const LifecycleEventSchema = z.object({
  id: Id,
  source: LifecycleEventSourceSchema,
  subjectId: Id,
  kind: z.string(),
  refType: z.string().nullable(),
  refId: z.string().nullable(),
  payload: z.unknown().nullable(),
  createdAt: IsoDateTime,
});
export type LifecycleEvent = z.infer<typeof LifecycleEventSchema>;

export const CustomerLifecycleMetricsSchema = z.object({
  userId: Id,
  stage: CustomerLifecycleStageSchema,
  /// Total lifetime value across all delivered orders, in paise.
  lifetimeValuePaise: Paise,
  orderCount: z.number().int().nonnegative(),
  /// Mean order value in paise. Zero when there are no orders yet.
  avgOrderValuePaise: Paise,
  firstOrderAt: IsoDateTime.nullable(),
  lastOrderAt: IsoDateTime.nullable(),
  lastSeenAt: IsoDateTime.nullable(),
  /// Days since last order (null when no orders).
  daysSinceLastOrder: z.number().int().nullable(),
});
export type CustomerLifecycleMetrics = z.infer<typeof CustomerLifecycleMetricsSchema>;

export const AbandonedCartSummarySchema = z.object({
  cartId: Id,
  userId: Id.nullable(),
  anonymousId: z.string().nullable(),
  itemCount: z.number().int().nonnegative(),
  estimatedValuePaise: Paise,
  abandonedAt: IsoDateTime,
});
export type AbandonedCartSummary = z.infer<typeof AbandonedCartSummarySchema>;

// ── Request shapes ─────────────────────────────────────────────────────────

export const RecordEventRequest = z
  .object({
    source: LifecycleEventSourceSchema,
    subjectId: Id,
    kind: z.string().trim().min(3).max(80),
    refType: z.string().trim().max(64).optional(),
    refId: z.string().trim().max(64).optional(),
    payload: z.record(z.unknown()).optional(),
  })
  .strict();
export type RecordEventRequest = z.infer<typeof RecordEventRequest>;

export const ListEventsQuery = z
  .object({
    kind: z.string().trim().min(3).max(80).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
  })
  .strict();
export type ListEventsQuery = z.infer<typeof ListEventsQuery>;

export const RunAbandonedCartScanRequest = z
  .object({
    /// Carts idle longer than this are abandoned. Default 2 hours per the lifecycle thesis.
    thresholdMinutes: z.number().int().min(15).max(7 * 24 * 60).default(120),
    /// Cap how many carts a single run will dispatch reminders to.
    maxDispatch: z.number().int().min(1).max(5000).default(500),
  })
  .strict();
export type RunAbandonedCartScanRequest = z.infer<typeof RunAbandonedCartScanRequest>;

export const AbandonedCartScanResultSchema = z.object({
  scannedCount: z.number().int().nonnegative(),
  abandonedCount: z.number().int().nonnegative(),
  nudgesDispatched: z.number().int().nonnegative(),
});
export type AbandonedCartScanResult = z.infer<typeof AbandonedCartScanResultSchema>;

// ── CRM Segments ───────────────────────────────────────────────────────────

export const CrmSegmentSchema = z.object({
  id: Id,
  name: z.string().min(1).max(128),
  description: z.string().nullable(),
  filters: z.record(z.unknown()), // unstructured JSON for now
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type CrmSegment = z.infer<typeof CrmSegmentSchema>;

export const CreateCrmSegmentRequest = z.object({
  name: z.string().min(1).max(128),
  description: z.string().optional(),
  filters: z.record(z.unknown()),
});
export type CreateCrmSegmentRequest = z.infer<typeof CreateCrmSegmentRequest>;

// ── CRM Campaigns ──────────────────────────────────────────────────────────

export const CrmCampaignStatusSchema = z.enum(['DRAFT', 'ACTIVE', 'PAUSED', 'COMPLETED']);
export type CrmCampaignStatus = z.infer<typeof CrmCampaignStatusSchema>;

export const CrmCampaignSchema = z.object({
  id: Id,
  name: z.string().min(1).max(128),
  description: z.string().nullable(),
  status: CrmCampaignStatusSchema,
  segmentId: Id.nullable(),
  journey: z.record(z.unknown()).nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type CrmCampaign = z.infer<typeof CrmCampaignSchema>;

export const CreateCrmCampaignRequest = z.object({
  name: z.string().min(1).max(128),
  description: z.string().optional(),
  segmentId: Id.optional(),
  journey: z.record(z.unknown()).optional(),
});
export type CreateCrmCampaignRequest = z.infer<typeof CreateCrmCampaignRequest>;

export const UpdateCrmCampaignRequest = z.object({
  name: z.string().min(1).max(128).optional(),
  description: z.string().optional(),
  status: CrmCampaignStatusSchema.optional(),
  segmentId: Id.optional(),
  journey: z.record(z.unknown()).optional(),
});
export type UpdateCrmCampaignRequest = z.infer<typeof UpdateCrmCampaignRequest>;

// ── CRM Consent ────────────────────────────────────────────────────────────

export const CommunicationChannelSchema = z.enum(['EMAIL', 'SMS', 'WHATSAPP', 'PUSH']);
export type CommunicationChannel = z.infer<typeof CommunicationChannelSchema>;

export const ConsentStatusSchema = z.enum(['OPT_IN', 'OPT_OUT']);
export type ConsentStatus = z.infer<typeof ConsentStatusSchema>;

export const CommunicationConsentSchema = z.object({
  id: Id,
  userId: Id,
  channel: CommunicationChannelSchema,
  status: ConsentStatusSchema,
  updatedAt: IsoDateTime,
});
export type CommunicationConsent = z.infer<typeof CommunicationConsentSchema>;

export const UpdateConsentRequest = z.object({
  channel: CommunicationChannelSchema,
  status: ConsentStatusSchema,
});
export type UpdateConsentRequest = z.infer<typeof UpdateConsentRequest>;
